import {
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  InboxOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Empty,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
  Tooltip,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { Container } from '../../../../../core/components';
import { UtilService } from '../../../../../core/services/util.service';
import { useProfile } from '../../profile/components/ProfileContext';
import { CandidateFile } from '../models/candidates.model';
import { CandidatesFilesService } from '../services/candidates-files.service';

interface CandidateFilesTableProps {
  isDeleted?: boolean;
  isBlocked?: boolean;
}

type CandidateFileRecord = CandidateFile & {
  fileType?: string;
  file?: Partial<CandidateFile> & { fileType?: string };
};

const FILE_TYPES = [
  { value: 'RESUME', label: 'Resume' },
  { value: 'COVER_LETTER', label: 'Cover letter' },
  { value: 'CERTIFICATION', label: 'Certification' },
  { value: 'OTHER', label: 'Other' },
];

function fileValue(
  record: CandidateFileRecord,
  property: keyof CandidateFileRecord
) {
  return record.file?.[property as keyof CandidateFile] ?? record[property];
}

export function formatCandidateFileType(value?: string) {
  if (!value) return 'Document';
  return value
    .toLowerCase()
    .split('_')
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function formatCandidateFileDate(value?: string) {
  if (!value) return 'Date unavailable';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value.split(' ')[0];
  }
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

function fileIcon(record: CandidateFileRecord) {
  const name = String(fileValue(record, 'name') || '').toLowerCase();
  const contentType = String(fileValue(record, 'contentType') || '');
  if (name.endsWith('.pdf') || contentType.includes('pdf')) {
    return <FilePdfOutlined />;
  }
  if (
    /\.(png|jpe?g|gif|webp)$/.test(name) ||
    contentType.startsWith('image/')
  ) {
    return <FileImageOutlined />;
  }
  return <FileTextOutlined />;
}

const CandidateFilesTable = forwardRef<
  { refreshFiles: () => void },
  CandidateFilesTableProps
>(({ isDeleted = false, isBlocked = false }, ref) => {
  const { id: candidateId } = useParams();
  const { isAdmin } = useProfile();
  const [candidateFiles, setCandidateFiles] = useState<CandidateFileRecord[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<string>();
  const disabled = isDeleted || isBlocked;

  const getFiles = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const files = await CandidatesFilesService.GetFiles(candidateId);
      setCandidateFiles(files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    void getFiles();
  }, [getFiles]);

  useImperativeHandle(ref, () => ({
    refreshFiles: getFiles,
  }));

  const resetForm = () => {
    setShowForm(false);
    setSelectedFile(null);
    setSelectedFileType(undefined);
  };

  const handleSave = async () => {
    if (!selectedFile || !selectedFileType || !candidateId) {
      UtilService.Alert(
        'Missing information',
        'info',
        'Select a file type and attach a file before uploading.'
      );
      return;
    }
    setSaving(true);
    try {
      await CandidatesFilesService.UploadFile(
        candidateId,
        selectedFile,
        selectedFileType
      );
      UtilService.Alert(
        'File uploaded',
        'success',
        'The document is now available on this candidate profile.'
      );
      resetForm();
      await getFiles();
    } catch (error) {
      console.error('Error saving file:', error);
      UtilService.Alert(
        'Upload failed',
        'error',
        'The file could not be uploaded. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteFile = async (file: CandidateFileRecord) => {
    if (!candidateId) return;
    const fileId = String(fileValue(file, 'id') || '');
    if (!fileId) return;
    try {
      await CandidatesFilesService.DeleteFile(candidateId, fileId);
      UtilService.Alert(
        'File deleted',
        'success',
        'The file was removed from this candidate.'
      );
      await getFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      UtilService.Alert(
        'Delete failed',
        'error',
        'The file could not be deleted.'
      );
    }
  };

  const handleDownload = async (file: CandidateFileRecord) => {
    const fileId = String(fileValue(file, 'id') || '');
    if (!candidateId || !fileId) return;
    try {
      const blob = await CandidatesFilesService.DownloadFile(
        candidateId,
        fileId
      );
      const url = window.URL.createObjectURL(blob);
      const filename = String(fileValue(file, 'name') || 'candidate-file');
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      UtilService.Alert(
        'Download failed',
        'error',
        'The file could not be downloaded.'
      );
    }
  };

  const columns: ColumnsType<CandidateFileRecord> = [
    {
      title: 'File',
      key: 'name',
      render: (_, record) => {
        const name = String(fileValue(record, 'name') || 'Untitled file');
        const contentType = String(
          fileValue(record, 'contentType') || 'Candidate document'
        );
        return (
          <div className="candidate-file-cell">
            <span className="candidate-file-cell__icon">
              {fileIcon(record)}
            </span>
            <div>
              <strong>{name}</strong>
              <span>{contentType}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Type',
      key: 'fileType',
      align: 'center',
      render: (_, record) => (
        <Tag className="candidate-file-type">
          {formatCandidateFileType(String(fileValue(record, 'fileType') || ''))}
        </Tag>
      ),
    },
    {
      title: 'Uploaded',
      key: 'createdDate',
      align: 'center',
      render: (_, record) =>
        formatCandidateFileDate(String(fileValue(record, 'createdDate') || '')),
    },
    {
      title: 'Uploaded by',
      key: 'createdBy',
      align: 'center',
      render: (_, record) => {
        const createdBy = String(fileValue(record, 'createdBy') || 'System');
        return (
          <div className="candidate-file-owner">
            <Avatar size={26}>{createdBy.charAt(0).toUpperCase()}</Avatar>
            <span>{createdBy}</span>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record) => (
        <div className="candidate-table-actions">
          <Tooltip title="Download file">
            <Button
              aria-label="Download file"
              icon={<DownloadOutlined />}
              disabled={disabled}
              onClick={() => void handleDownload(record)}
            />
          </Tooltip>
          {isAdmin ? (
            <Popconfirm
              title="Delete this file?"
              description="This action cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={() => void deleteFile(record)}
            >
              <Tooltip title="Delete file">
                <Button
                  danger
                  aria-label="Delete file"
                  icon={<DeleteOutlined />}
                  disabled={disabled}
                />
              </Tooltip>
            </Popconfirm>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <Container>
      <section className="candidate-section candidate-files">
        <header className="candidate-section__header">
          <div>
            <span className="candidate-section__eyebrow">
              Candidate library
            </span>
            <h2>Files</h2>
            <p>
              Keep resumes, cover letters, certifications, and supporting
              documents together.
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={disabled}
            onClick={() => setShowForm(true)}
          >
            Upload file
          </Button>
        </header>

        <div className="candidate-files__summary">
          <span>
            <FileTextOutlined />
          </span>
          <div>
            <strong>{candidateFiles.length} documents</strong>
            <p>
              {disabled
                ? 'This profile is read-only.'
                : 'Files are available to authorized recruiters.'}
            </p>
          </div>
          <SafetyCertificateOutlined />
        </div>

        <div className="candidate-table-shell">
          <Table<CandidateFileRecord>
            columns={columns}
            dataSource={candidateFiles}
            rowKey={(record) => String(fileValue(record, 'id') || '')}
            loading={loading}
            pagination={false}
            scroll={{ x: 760 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No candidate files yet"
                >
                  {!disabled ? (
                    <Button
                      type="primary"
                      icon={<CloudUploadOutlined />}
                      onClick={() => setShowForm(true)}
                    >
                      Upload the first file
                    </Button>
                  ) : null}
                </Empty>
              ),
            }}
          />
        </div>

        <Modal
          open={showForm}
          title={
            <div className="candidate-upload-title">
              <span>
                <CloudUploadOutlined />
              </span>
              <div>
                <strong>Upload candidate file</strong>
                <p>Add a document to the candidate’s shared profile.</p>
              </div>
            </div>
          }
          okText="Upload file"
          okButtonProps={{
            icon: <CloudUploadOutlined />,
            disabled: !selectedFile || !selectedFileType,
          }}
          confirmLoading={saving}
          onOk={() => void handleSave()}
          onCancel={resetForm}
          destroyOnClose
        >
          <div className="candidate-upload-form">
            <label htmlFor="candidate-file-type">Document type</label>
            <Select
              id="candidate-file-type"
              options={FILE_TYPES}
              placeholder="Select a document type"
              value={selectedFileType}
              onChange={setSelectedFileType}
            />
            <label>File</label>
            <Upload.Dragger
              multiple={false}
              maxCount={1}
              beforeUpload={(nextFile) => {
                setSelectedFile(nextFile);
                return false;
              }}
              onRemove={() => setSelectedFile(null)}
              fileList={selectedFile ? [selectedFile as any] : []}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Drag and drop a file here, or click to browse
              </p>
              <p className="ant-upload-hint">
                PDF, Word, and common image formats are supported.
              </p>
            </Upload.Dragger>
          </div>
        </Modal>
      </section>
    </Container>
  );
});

CandidateFilesTable.displayName = 'CandidateFilesTable';

export default CandidateFilesTable;
